import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { FileSpreadsheet, Pencil, Plus, Send, Trash2, Upload, UserPlus, XCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ImportReviewDialog } from '@/components/ui/modules/import-review-dialog';
import { CreateUserDialog, CreateUserDialogValues } from '@/pages/admin/userAccounts/components/CreateUserDialog';
import { buildLegacyAwareAssignments } from '@/pages/admin/userAccounts/legacyPolicyAssignments';
import { LIST_POLICY_ALIASES } from '@/pages/admin/userAccounts/queries';
import {
  findMatchingOrganizationalAlias,
  formatOrganizationalAliasLabel
} from '@/pages/admin/userAccounts/organizationalAliases';
import {
  mapActiveOptionLabels,
  sortRequestOptionsAlphabetically,
  sortRequestOptionsByConfiguredOrder
} from './optionSorting';

const LIST_ACCOUNT_REQUESTS = gql`
  query UserAccountRequests($filters: UserAccountRequestFiltersInput) {
    userAccountRequests(filters: $filters) {
      totalCount
      entries {
        id
        requestType
        requesterName
        requestedName
        emailAddress
        organizationName
        siteName
        programName
        roleName
        accessReason
        userDateCreated
        deactivationDate
        deactivationReason
        createdByUsername
        status
        createdAt
      }
    }
  }
`;

const CREATE_ACCOUNT_REQUEST = gql`
  mutation CreateUserAccountRequest($input: CreateUserAccountRequestInput!) {
    createUserAccountRequest(input: $input) {
      success
      message
      requestId
    }
  }
`;

const LIST_USER_ACCOUNT_REQUEST_OPTIONS = gql`
  query UserAccountRequestFormOptions {
	    roleOptions: userAccountRequestOptions(optionType: "role") {
	      id
	      optionType
	      label
	      sortOrder
	      isActive
	      defaultPrograms
	    }
	    organizationOptions: userAccountRequestOptions(optionType: "organization") {
	      id
	      optionType
	      label
	      sortOrder
	      isActive
	      defaultPrograms
	    }
	    siteOptions: userAccountRequestOptions(optionType: "site") {
	      id
	      optionType
	      label
	      sortOrder
	      isActive
	      defaultPrograms
	    }
	  }
	`;

const CONVERT_ACCOUNT_REQUEST = gql`
  mutation ConvertUserAccountRequest($input: ConvertUserAccountRequestInput!) {
    convertUserAccountRequest(input: $input) {
      success
      message
      requestId
      createdUserId
      username
      temporaryPassword
    }
  }
`;

const CANCEL_ACCOUNT_REQUEST = gql`
  mutation CancelUserAccountRequest($input: CancelUserAccountRequestInput!) {
    cancelUserAccountRequest(input: $input) {
      success
      message
      requestId
    }
  }
`;

const UPDATE_ACCOUNT_REQUEST = gql`
  mutation UpdateUserAccountRequest($input: UpdateUserAccountRequestInput!) {
    updateUserAccountRequest(input: $input) {
      success
      message
      requestId
    }
  }
`;

const PROGRAM_OPTIONS = ['FIST', 'MIST', 'ODRH', 'Maternal Health', 'LEAD'];
const ACCESS_PROGRAM_OPTIONS = [...PROGRAM_OPTIONS, 'DSH'];
const REQUEST_TYPE_ACTIVATION = 'activation';
const REQUEST_TYPE_DEACTIVATION = 'deactivation';
const IMPORT_REQUEST_CONCURRENCY = 8;
const PROGRAM_ACCESS_POLICIES: Record<string, string[]> = {
  FIST: ['legacy:fistaccess'],
  MIST: ['legacy:diversionaccess'],
  ODRH: ['legacy:odrhousingaccess'],
  'Maternal Health': ['legacy:prgwmnaccess'],
  LEAD: ['legacy:leadaccess'],
  DSH: ['legacy:dshaccess']
};
const ORGANIZATION_PROGRAM_TEMPLATES: Record<string, string[]> = {
  dhs: ACCESS_PROGRAM_OPTIONS,
  'la county dhs': ACCESS_PROGRAM_OPTIONS,
  'los angeles county department of health services': ACCESS_PROGRAM_OPTIONS,
  'department of health services': ACCESS_PROGRAM_OPTIONS
};

const REQUEST_HANDSHAKE_POLICY_CHART = {
  defaultPolicies: ['User'],
  programPolicies: PROGRAM_ACCESS_POLICIES,
  organizationRoleExtraPolicies: {} as Record<string, string[]>
};

const REQUEST_FORM_PROGRAM_BY_ORG_ROLE = {
  'alcott center|housing director': 'ODRH',
  'alcott center|program manager': 'ODRH',
  'alma family services|referral coordinator ela': 'LEAD',
  'alma family services|referral coordinator long beach/sla': 'LEAD',
  'amity foundation|coordinator': 'ODRH',
  "brown's bridge housing|ih program manager": 'ODRH',
  'christ centered ministries|clinician': 'FIST',
  'christ-centered ministries|clinician': 'FIST',
  'exodus recovery|director': 'ODRH',
  'exodus recovery, inc.|director': 'ODRH',
  'first to serve|ih program manager': 'ODRH',
  'first to serve|program manager': 'ODRH',
  'first to serve, inc.|ih program manager': 'ODRH',
  'first to serve, inc.|program manager': 'ODRH',
  'generations health|director of nursing': 'FIST',
  'generations health|program director': 'FIST',
  'generations health|program manager': 'FIST',
  'joyous ventures|program manager': 'ODRH',
  'love home inc.|ih program manager': 'ODRH',
  'love home, inc.|ih program manager': 'ODRH',
  'project 180|program coordinator': 'ODRH',
  'project 180|program supervisor': 'ODRH',
  'project 180|senior clinical supervisor': 'ODRH',
  'reach for the top|ih program manager': 'ODRH',
  'reach for the top, inc.|ih program manager': 'ODRH',
  'sleep tight tonight|clinician': 'FIST',
  'sleep tight tonight|program manager': 'FIST',
  'the people concern|clinical supervisor': 'ODRH',
  'the people concern|program manager': 'ODRH',
  'timelist group|ih program manager': 'ODRH',
  'timelist group, inc.|ih program manager': 'ODRH',
  'velnonart transformative health|clinical supervisor': 'LEAD',
  'velnonart transformative health|referral coordinator': 'LEAD'
} as Record<string, string>;

export type UserAccountRequestRow = {
  id: number;
  requestType?: string | null;
  requesterName: string;
  requestedName: string;
  emailAddress: string;
  organizationName: string;
  siteName?: string | null;
  programName: string;
  roleName: string;
  accessReason: string;
  userDateCreated?: string | null;
  deactivationDate?: string | null;
  deactivationReason?: string | null;
  createdByUsername?: string | null;
  status: string;
  createdAt?: string | null;
};

type UserAccountRequestsResponse = {
  userAccountRequests: {
    totalCount: number;
    entries: UserAccountRequestRow[];
  };
};

type UserAccountRequestOption = {
  id: number;
  optionType: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  defaultPrograms?: string[] | null;
};

type UserAccountRequestOptionsResponse = {
  roleOptions: UserAccountRequestOption[];
  organizationOptions: UserAccountRequestOption[];
  siteOptions: UserAccountRequestOption[];
};

type CreateUserAccountResponse = {
  createUserAccountRequest?: {
    success?: boolean;
    message?: string;
    requestId?: number;
  };
};

type ConvertUserAccountResponse = {
  convertUserAccountRequest?: {
    success?: boolean;
    message?: string;
    requestId?: number;
    createdUserId?: number;
    username?: string;
    temporaryPassword?: string | null;
  };
};

export type UserAccountProvisionResult = {
  requestId: number;
  createdUserId?: number;
  username?: string;
};

type PolicyAliasRecord = {
  alias: string;
  type?: string | null;
  active?: boolean | null;
  policies?: string[] | null;
};

type CancelUserAccountResponse = {
  cancelUserAccountRequest?: {
    success?: boolean;
    message?: string;
    requestId?: number;
  };
};

type UpdateUserAccountRequestResponse = {
  updateUserAccountRequest?: {
    success?: boolean;
    message?: string;
    requestId?: number;
  };
};

type ImportDraftRow = {
  rowNumber: number;
  requestedName: string;
  emailAddress: string;
  roleName: string;
  organizationName: string;
  siteName: string;
  accessReason: string;
};

type ImportEditableField = Exclude<keyof ImportDraftRow, 'rowNumber'>;
type ImportDestination = 'review' | 'batch';

type PreparedImportRow = {
  rowNumber: number;
  requestedName: string;
  emailAddress: string;
  roleName: string;
  organizationName: string;
  siteName: string;
  accessReason: string;
  error: string;
};

const emptyBatchDraft: Omit<ImportDraftRow, 'rowNumber'> = {
  requestedName: '',
  emailAddress: '',
  roleName: '',
  organizationName: '',
  siteName: '',
  accessReason: ''
};

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString();
};

const truncate = (value: string, max = 80) => {
  if (!value) return '';
  if (value.length <= max) return value;
  return `${value.slice(0, max)}...`;
};

const clean = (value: string) => value.trim();
const cleanImportValue = (value: unknown) => `${value === null || value === undefined ? '' : value}`.replace(/\u00a0/g, ' ').trim();
const truncateReason = (value: unknown) => truncate(cleanImportValue(value), 30);
const normalizeImportLookup = (value: string) => cleanImportValue(value).replace(/\s+/g, ' ').toLowerCase();
const normalizeImportHeader = (value: string) => normalizeImportLookup(value).replace(/[^a-z0-9]/g, '');
const normalizeProgramName = (value: string) => {
  const normalized = normalizeImportLookup(value);
  if (!normalized) return '';
  if (normalized === 'fist') return 'FIST';
  if (normalized === 'mist') return 'MIST';
  if (normalized === 'odrh') return 'ODRH';
  if (normalized === 'maternal health' || normalized === 'maternalhealth') return 'Maternal Health';
  if (normalized === 'lead') return 'LEAD';
  return '';
};
const normalizeAccessProgramName = (value: string) => {
  const primaryProgram = normalizeProgramName(value);
  if (primaryProgram) return primaryProgram;
  const normalized = normalizeImportLookup(value);
  if (normalized === 'dsh' || normalized === 'dsh diversion') return 'DSH';
  return '';
};
const matchConfiguredOption = (value: string, options: string[]) => {
  const normalizedValue = normalizeImportLookup(value);
  if (!normalizedValue) return '';

  for (const option of options || []) {
    if (normalizeImportLookup(option) !== normalizedValue) continue;
    return option;
  }

  return '';
};

const getImportRowValue = (row: Record<string, unknown>, aliases: string[]) => {
  const entries = Object.entries(row || {});
  if (entries.length === 0) return '';

  for (const alias of aliases) {
    const aliasToken = normalizeImportHeader(alias);
    if (!aliasToken) continue;

    for (const [rawKey, rawValue] of entries) {
      if (normalizeImportHeader(rawKey) !== aliasToken) continue;
      return cleanImportValue(rawValue);
    }
  }

  return '';
};

const importFieldHasError = (error: string, aliases: string[]) => {
  const normalizedError = `${error || ''}`.toLowerCase();
  if (!normalizedError) return false;
  return aliases.some((alias) => normalizedError.includes(alias.toLowerCase()));
};

const IMPORT_COLUMN_ALIASES = {
  requestedName: ['User Name', 'Requested Name', 'Name'],
  emailAddress: ['User Email', 'Email', 'Email Address'],
  roleName: ['Role', 'Role Name'],
  organizationName: ['Organization', 'Organization Name'],
  siteName: ['Site', 'Site Name'],
  accessReason: ['Reason', 'Access Reason', 'Reason for request', 'Reason for requesting access']
};

const isDeactivationRequest = (requestType?: string | null) => {
  return `${requestType || ''}`.trim().toLowerCase() === REQUEST_TYPE_DEACTIVATION;
};

const requestTypeLabel = (requestType?: string | null) => {
  if (isDeactivationRequest(requestType)) return 'Deactivation';
  return 'Activation';
};

const requestStatusBadgeClass = (status?: string | null) => {
  const normalizedStatus = `${status || ''}`.trim().toLowerCase();
  if (normalizedStatus === 'pending') {
    return 'border border-[var(--semantic-warning-border-soft)] bg-[var(--semantic-warning-soft)] text-[var(--semantic-warning)]';
  }
  if (normalizedStatus === 'converted') {
    return 'border border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (normalizedStatus === 'deactivated') {
    return 'border border-[var(--semantic-error-border-soft)] bg-[var(--semantic-error-soft)] text-[var(--semantic-error)]';
  }
  if (normalizedStatus === 'canceled') {
    return 'border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] text-[var(--neutral-gray-90)]';
  }
  return 'border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] text-[var(--neutral-gray-90)]';
};

const parsePolicyList = (value: string[]): string[] => {
  const parts = (value || [])
    .map((item) => `${item || ''}`.trim())
    .filter((item) => item.length > 0);
  return Array.from(new Set(parts));
};

const normalizeChartToken = (value?: string | null) => {
  return `${value || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');
};

const normalizeProgramLookupToken = (value?: string | null) => {
  return `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ');
};

const normalizeProgramList = (values?: string[] | null) => {
  const programs: string[] = [];
  const seen = new Set<string>();

  (Array.isArray(values) ? values : []).forEach((value) => {
    const program = normalizeAccessProgramName(value);
    if (!program || seen.has(program)) return;
    seen.add(program);
    programs.push(program);
  });

  return programs;
};

const REQUEST_FORM_PROGRAM_BY_ORG_ROLE_NORMALIZED = Object.entries(REQUEST_FORM_PROGRAM_BY_ORG_ROLE).reduce((acc, entry) => {
  const [rawKey, programName] = entry;
  const [organizationName = '', roleName = ''] = rawKey.split('|');
  const normalizedKey = `${normalizeProgramLookupToken(organizationName)}|${normalizeProgramLookupToken(roleName)}`;
  if (!normalizedKey.trim() || !programName) return acc;
  acc[normalizedKey] = programName;
  return acc;
}, {} as Record<string, string>);

const REQUEST_FORM_PROGRAM_BY_ORGANIZATION_NORMALIZED = Object.entries(REQUEST_FORM_PROGRAM_BY_ORG_ROLE).reduce((acc, entry) => {
  const [rawKey, programName] = entry;
  const [organizationName = ''] = rawKey.split('|');
  const normalizedOrganization = normalizeProgramLookupToken(organizationName);
  if (!normalizedOrganization || !programName) return acc;
  const current = acc[normalizedOrganization];
  if (!current) {
    acc[normalizedOrganization] = programName;
    return acc;
  }
  if (current === programName) return acc;
  acc[normalizedOrganization] = '';
  return acc;
}, {} as Record<string, string>);

const ORGANIZATION_PROGRAM_TEMPLATES_NORMALIZED = Object.entries(ORGANIZATION_PROGRAM_TEMPLATES).reduce((acc, entry) => {
  const [organizationName, programs] = entry;
  const normalizedOrganization = normalizeProgramLookupToken(organizationName);
  const normalizedPrograms = normalizeProgramList(programs);
  if (!normalizedOrganization || normalizedPrograms.length === 0) return acc;
  acc[normalizedOrganization] = normalizedPrograms;
  return acc;
}, {} as Record<string, string[]>);

const buildOrganizationProgramTemplatesFromOptions = (options?: UserAccountRequestOption[] | null) => {
  const templates: Record<string, string[]> = { ...ORGANIZATION_PROGRAM_TEMPLATES_NORMALIZED };

  (Array.isArray(options) ? options : []).forEach((option) => {
    if (!option || option.isActive === false) return;
    const normalizedOrganization = normalizeProgramLookupToken(option.label);
    const programs = normalizeProgramList(option.defaultPrograms);
    if (!normalizedOrganization || programs.length === 0) return;
    templates[normalizedOrganization] = programs;
  });

  return templates;
};

const inferProgramsBySiteName = (siteName?: string | null) => {
  const normalizedSite = ` ${normalizeProgramLookupToken(siteName)} `;
  if (!normalizedSite.trim()) return [];

  const programs: string[] = [];
  const addProgram = (program: string) => {
    if (!programs.includes(program)) programs.push(program);
  };

  if (/\bfist\b/.test(normalizedSite)) addProgram('FIST');
  if (/\bmist\b/.test(normalizedSite)) addProgram('MIST');
  if (/\bodrh\b/.test(normalizedSite) || /\bodr housing\b/.test(normalizedSite) || /\bodr reentry\b/.test(normalizedSite)) addProgram('ODRH');
  if (/\blead\b/.test(normalizedSite)) addProgram('LEAD');
  if (/\bmaternal\b/.test(normalizedSite) || /\bpw\b/.test(normalizedSite) || /\bpregnant women\b/.test(normalizedSite)) addProgram('Maternal Health');

  return programs;
};

const inferProgramsByOrganizationRole = (
  organizationName?: string | null,
  roleName?: string | null,
  siteName?: string | null,
  organizationProgramTemplates: Record<string, string[]> = ORGANIZATION_PROGRAM_TEMPLATES_NORMALIZED
) => {
  const normalizedOrganization = normalizeProgramLookupToken(organizationName);
  const normalizedRole = normalizeProgramLookupToken(roleName);
  const normalizedKey = `${normalizedOrganization}|${normalizedRole}`;

  const exactProgram = normalizeProgramName(REQUEST_FORM_PROGRAM_BY_ORG_ROLE_NORMALIZED[normalizedKey] || '');
  if (exactProgram) return [exactProgram];

  const organizationTemplate = organizationProgramTemplates[normalizedOrganization];
  if (Array.isArray(organizationTemplate) && organizationTemplate.length > 0) {
    return organizationTemplate;
  }

  const sitePrograms = inferProgramsBySiteName(siteName);
  if (sitePrograms.length > 0) return sitePrograms;

  const organizationDefault = normalizeProgramName(REQUEST_FORM_PROGRAM_BY_ORGANIZATION_NORMALIZED[normalizedOrganization] || '');
  return organizationDefault ? [organizationDefault] : [];
};

const normalizeProgramForPolicyChart = (value?: string | null) => {
  const normalized = normalizeChartToken(value);
  if (!normalized) return '';
  if (normalized === 'fist') return 'FIST';
  if (normalized === 'mist') return 'MIST';
  if (normalized === 'odrh') return 'ODRH';
  if (normalized === 'maternal health') return 'Maternal Health';
  if (normalized === 'lead') return 'LEAD';
  return '';
};

const buildHandshakePoliciesForRequest = (
  request?: UserAccountRequestRow | null,
  organizationProgramTemplates: Record<string, string[]> = ORGANIZATION_PROGRAM_TEMPLATES_NORMALIZED
): string[] => {
  if (!request) return [];

  const programKeys = Array.from(new Set([
    normalizeProgramForPolicyChart(request.programName),
    ...inferProgramsByOrganizationRole(request.organizationName, request.roleName, request.siteName, organizationProgramTemplates)
  ].filter((program) => program.length > 0)));
  const orgRoleKey = `${normalizeChartToken(request.organizationName)}|${normalizeChartToken(request.roleName)}`;

  const combined = [
    ...REQUEST_HANDSHAKE_POLICY_CHART.defaultPolicies,
    ...programKeys.flatMap((programKey) => REQUEST_HANDSHAKE_POLICY_CHART.programPolicies[programKey] || []),
    ...(REQUEST_HANDSHAKE_POLICY_CHART.organizationRoleExtraPolicies[orgRoleKey] || [])
  ];

  return parsePolicyList(combined);
};

type ProvisionUserAccountRequestDialogProps = {
  request: UserAccountRequestRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProvisioned?: (result: UserAccountProvisionResult) => void;
};

export const ProvisionUserAccountRequestDialog: React.FC<ProvisionUserAccountRequestDialogProps> = ({
  request,
  open,
  onOpenChange,
  onProvisioned
}) => {
  const { data: optionData } = useQuery<UserAccountRequestOptionsResponse>(LIST_USER_ACCOUNT_REQUEST_OPTIONS, {
    fetchPolicy: 'cache-and-network',
    skip: !open
  });
  const [convertRequest, { loading: converting }] = useMutation<ConvertUserAccountResponse>(CONVERT_ACCOUNT_REQUEST);
  const organizationProgramTemplates = React.useMemo(() => {
    return buildOrganizationProgramTemplatesFromOptions(optionData?.organizationOptions);
  }, [optionData?.organizationOptions]);
  const initialSelectedPolicies = React.useMemo(() => {
    return buildHandshakePoliciesForRequest(request, organizationProgramTemplates);
  }, [organizationProgramTemplates, request]);

  const handleConvert = async (values: CreateUserDialogValues) => {
    if (!request) {
      toast.error('Account request is unavailable');
      return;
    }

    try {
      const response = await convertRequest({
        variables: {
          input: {
            requestId: request.id,
            username: values.username,
            emailAddress: values.emailAddress,
            organization: values.organization || null,
            site: values.site || null,
            twoFactorMethod: values.twoFactorMethod,
            legacyAccesses: values.legacyAccesses,
            selectedPolicies: values.selectedPolicies,
            activateNow: true
          }
        }
      });
      const result = response?.data?.convertUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to convert request');
        return;
      }

      toast.success('User created');
      onProvisioned?.({
        requestId: request.id,
        createdUserId: result.createdUserId,
        username: result.username
      });
      onOpenChange(false);
    } catch (convertError: any) {
      toast.error(convertError?.message || 'Failed to convert request');
    }
  };

  const contextPanel = request ? (
    <div className="rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--neutral-gray-100)]">
      <div className="grid grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-4">
        <div><strong>Request:</strong> #{request.id}</div>
        <div><strong>Requester:</strong> {request.requesterName}</div>
        <div><strong>Role:</strong> {request.roleName}</div>
        <div><strong>User:</strong> {request.requestedName}</div>
        <div><strong>Organization:</strong> {request.organizationName}</div>
        <div><strong>Site:</strong> {request.siteName || 'N/A'}</div>
        <div className="md:col-span-4"><strong>Reason:</strong> {request.accessReason || 'N/A'}</div>
      </div>
    </div>
  ) : null;

  return (
    <CreateUserDialog
      open={Boolean(open && request && !isDeactivationRequest(request.requestType))}
      onOpenChange={onOpenChange}
      onSubmit={handleConvert}
      submitting={converting}
      title={request ? `Create User Account for Request #${request.id}` : 'Create User Account'}
      description="Review the account settings and create the requested user."
      submitLabel="Create User Account"
      showRealNameField={false}
      contextPanel={contextPanel}
      initialValues={request ? {
        emailAddress: request.emailAddress || '',
        organization: request.organizationName || '',
        site: request.siteName || '',
        roleName: request.roleName || '',
        legacyAccesses: buildLegacyAwareAssignments(initialSelectedPolicies).legacyPermissions.map((entry) => entry.permission),
        selectedPolicies: initialSelectedPolicies
      } : undefined}
    />
  );
};

export const RequestUserAccountPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [roleFilter, setRoleFilter] = React.useState('all');
  const [organizationFilter, setOrganizationFilter] = React.useState('all');
  const [siteFilter, setSiteFilter] = React.useState('all');
  const [pendingDeleteRequestId, setPendingDeleteRequestId] = React.useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [deleteReason, setDeleteReason] = React.useState('');
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = React.useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = React.useState('');
  const [selectedPendingRequestIds, setSelectedPendingRequestIds] = React.useState<number[]>([]);
  const [editingRequest, setEditingRequest] = React.useState<UserAccountRequestRow | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editRoleName, setEditRoleName] = React.useState('');
  const [editSiteName, setEditSiteName] = React.useState('');
  const [editAccessReason, setEditAccessReason] = React.useState('');
  const [isNewRequestDialogOpen, setIsNewRequestDialogOpen] = React.useState(false);
  const [batchDraft, setBatchDraft] = React.useState<Omit<ImportDraftRow, 'rowNumber'>>(() => emptyBatchDraft);
  const [isImportDialogOpen, setIsImportDialogOpen] = React.useState(false);
  const [importRows, setImportRows] = React.useState<ImportDraftRow[]>([]);
  const [importSourceName, setImportSourceName] = React.useState('');
  const [importServerErrors, setImportServerErrors] = React.useState<Record<number, string>>({});
  const [importDestination, setImportDestination] = React.useState<ImportDestination>('review');
  const [isImporting, setIsImporting] = React.useState(false);
  const importFileInputRef = React.useRef<HTMLInputElement | null>(null);

  const { data, loading, error, refetch } = useQuery<UserAccountRequestsResponse>(LIST_ACCOUNT_REQUESTS, {
    variables: {
      filters: {
	        status: statusFilter,
	        search: searchTerm.trim(),
	        roleName: roleFilter === 'all' ? null : roleFilter,
	        organizationName: organizationFilter === 'all' ? null : organizationFilter,
	        siteName: siteFilter === 'all' ? null : siteFilter,
        limit: 100,
        offset: 0,
        createdByMe: true
      }
    },
    fetchPolicy: 'cache-and-network'
  });

  const {
    data: optionData,
    loading: optionsLoading,
    error: optionsError
  } = useQuery<UserAccountRequestOptionsResponse>(LIST_USER_ACCOUNT_REQUEST_OPTIONS, {
    fetchPolicy: 'cache-and-network'
  });

  const [createRequest, { loading: submitting }] = useMutation<CreateUserAccountResponse>(CREATE_ACCOUNT_REQUEST);
  const [cancelRequest, { loading: cancelingRequest }] = useMutation<CancelUserAccountResponse>(CANCEL_ACCOUNT_REQUEST);
  const [updateRequest, { loading: updatingRequest }] = useMutation<UpdateUserAccountRequestResponse>(UPDATE_ACCOUNT_REQUEST);

  const requests = data?.userAccountRequests?.entries || [];
  const totalCount = data?.userAccountRequests?.totalCount || 0;
  const pendingRequestIdsOnPage = requests
    .filter((row) => row.status === 'pending')
    .map((row) => row.id);
  const allPendingSelected = pendingRequestIdsOnPage.length > 0
    && pendingRequestIdsOnPage.every((id) => selectedPendingRequestIds.includes(id));

  React.useEffect(() => {
    setSelectedPendingRequestIds((prev) => {
      if (!prev.length) return prev;
      const allowed = new Set(pendingRequestIdsOnPage);
      const next = prev.filter((id) => allowed.has(id));
      if (next.length === prev.length) return prev;
      return next;
    });
  }, [pendingRequestIdsOnPage]);

  const organizationOptions = React.useMemo(() => {
    return mapActiveOptionLabels(optionData?.organizationOptions, sortRequestOptionsAlphabetically);
  }, [optionData?.organizationOptions]);

  const roleOptions = React.useMemo(() => {
    return mapActiveOptionLabels(optionData?.roleOptions, sortRequestOptionsByConfiguredOrder);
  }, [optionData?.roleOptions]);

  const siteOptions = React.useMemo(() => {
    return mapActiveOptionLabels(optionData?.siteOptions, sortRequestOptionsAlphabetically);
  }, [optionData?.siteOptions]);

  React.useEffect(() => {
    if (optionsLoading) return;

    setBatchDraft((prev) => {
      if (!prev.organizationName) return prev;
      if (organizationOptions.includes(prev.organizationName)) return prev;
      return {
        ...prev,
        organizationName: ''
      };
    });
  }, [optionsLoading, organizationOptions]);

  React.useEffect(() => {
    if (optionsLoading) return;

    setBatchDraft((prev) => {
      if (!prev.siteName) return prev;
      if (siteOptions.includes(prev.siteName)) return prev;
      return {
        ...prev,
        siteName: ''
      };
    });
  }, [optionsLoading, siteOptions]);

  React.useEffect(() => {
    if (optionsLoading) return;

    setBatchDraft((prev) => {
      if (!prev.roleName) return prev;
      if (roleOptions.includes(prev.roleName)) return prev;
      return {
        ...prev,
        roleName: ''
      };
    });
  }, [optionsLoading, roleOptions]);

  React.useEffect(() => {
    if (roleFilter === 'all') return;
    if (roleOptions.includes(roleFilter)) return;
    setRoleFilter('all');
  }, [roleFilter, roleOptions]);

  React.useEffect(() => {
    if (organizationFilter === 'all') return;
    if (organizationOptions.includes(organizationFilter)) return;
    setOrganizationFilter('all');
  }, [organizationFilter, organizationOptions]);

  React.useEffect(() => {
    if (siteFilter === 'all') return;
    if (siteOptions.includes(siteFilter)) return;
    setSiteFilter('all');
  }, [siteFilter, siteOptions]);

  const setBatchDraftField = (key: ImportEditableField, value: string) => {
    setBatchDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleDeleteRequest = async (requestId: number, reason: string) => {
    try {
      const response = await cancelRequest({
        variables: { input: { requestId, cancelReason: reason } }
      });
      const result = response?.data?.cancelUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to delete request');
        return;
      }
      setSelectedPendingRequestIds((prev) => prev.filter((id) => id !== requestId));
      toast.success(`Request #${requestId} deleted`);
      await refetch();
    } catch (deleteError: any) {
      toast.error(deleteError?.message || 'Failed to delete request');
    }
  };

  const setPendingRequestSelected = (requestId: number, selected: boolean) => {
    setSelectedPendingRequestIds((prev) => {
      if (selected && prev.includes(requestId)) return prev;
      if (selected) return [...prev, requestId];
      return prev.filter((id) => id !== requestId);
    });
  };

  const setAllPendingRequestsSelected = (selected: boolean) => {
    if (!selected) {
      setSelectedPendingRequestIds([]);
      return;
    }
    setSelectedPendingRequestIds(pendingRequestIdsOnPage);
  };

  const openDeleteDialog = (requestId: number) => {
    setPendingDeleteRequestId(requestId);
    setDeleteReason('');
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteRequest = async () => {
    const requestId = pendingDeleteRequestId;
    if (!requestId) return;
    const reason = deleteReason.trim();
    if (!reason) {
      toast.error('Delete notes are required');
      return;
    }

    setPendingDeleteRequestId(null);
    setDeleteReason('');
    setIsDeleteDialogOpen(false);
    await handleDeleteRequest(requestId, reason);
  };

  const openBulkDeleteDialog = () => {
    if (!selectedPendingRequestIds.length) {
      toast.error('Select at least one pending request');
      return;
    }
    setBulkDeleteReason('');
    setIsBulkDeleteDialogOpen(true);
  };

  const confirmBulkDeleteRequests = async () => {
    const requestIds = [...selectedPendingRequestIds];
    if (!requestIds.length) return;
    const reason = bulkDeleteReason.trim();
    if (!reason) {
      toast.error('Delete notes are required');
      return;
    }

    setIsBulkDeleteDialogOpen(false);
    setBulkDeleteReason('');

    const failures: string[] = [];
    let successCount = 0;

    for (const requestId of requestIds) {
      try {
        const response = await cancelRequest({
          variables: { input: { requestId, cancelReason: reason } }
        });
        const result = response?.data?.cancelUserAccountRequest;
        if (!result?.success) {
          failures.push(`#${requestId}: ${result?.message || 'Unable to delete request'}`);
          continue;
        }
        successCount += 1;
      } catch (deleteError: any) {
        failures.push(`#${requestId}: ${deleteError?.message || 'Failed to delete request'}`);
      }
    }

    setSelectedPendingRequestIds([]);
    await refetch();

    if (successCount > 0) {
      const requestLabel = successCount === 1 ? 'request' : 'requests';
      toast.success(`Deleted ${successCount} ${requestLabel}`);
    }
    if (!failures.length) return;
    toast.error(`Some requests were not deleted. ${failures[0]}`);
  };

  const openEditDialog = (request: UserAccountRequestRow) => {
    if (request.status !== 'pending') return;
    if (isDeactivationRequest(request.requestType)) return;

    setEditingRequest(request);
    setEditRoleName(request.roleName || '');
    setEditSiteName(request.siteName || '');
    setEditAccessReason(request.accessReason || '');
    setIsEditDialogOpen(true);
  };

  const submitEditRequest = async () => {
    const request = editingRequest;
    if (!request) return;

    const roleName = editRoleName.trim();
    const siteName = editSiteName.trim();
    const accessReason = editAccessReason.trim();
    if (!roleName) {
      toast.error('Role is required');
      return;
    }
    if (!accessReason) {
      toast.error('Notes are required');
      return;
    }

    try {
      const response = await updateRequest({
        variables: {
          input: {
            requestId: request.id,
            roleName,
            siteName: siteName || null,
            accessReason
          }
        }
      });
      const result = response?.data?.updateUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to update request');
        return;
      }

      toast.success(`Request #${request.id} updated`);
      setIsEditDialogOpen(false);
      setEditingRequest(null);
      setEditRoleName('');
      setEditSiteName('');
      setEditAccessReason('');
      await refetch();
    } catch (submitError: any) {
      toast.error(submitError?.message || 'Failed to update request');
    }
  };

  const prepareImportRow = (row: ImportDraftRow): PreparedImportRow => {
    const requestedName = clean(row.requestedName);
    const emailAddress = clean(row.emailAddress);
	    const roleName = clean(row.roleName);
	    const organizationName = clean(row.organizationName);
	    const siteName = clean(row.siteName);
	    const accessReason = clean(row.accessReason);

    const fail = (error: string): PreparedImportRow => ({
      rowNumber: row.rowNumber,
      requestedName,
      emailAddress,
	      roleName,
	      organizationName,
	      siteName,
	      accessReason,
	      error
    });

    if (!requestedName) return fail('User Name is required');
    if (!emailAddress) return fail('User Email is required');
    if (!roleName) return fail('Role is required');
    if (!organizationName) return fail('Organization is required');

	    if (!roleOptions.length) return fail('No role options are configured');
    const matchedRoleName = matchConfiguredOption(roleName, roleOptions);
    if (!matchedRoleName) return fail(`Role "${roleName}" is not configured`);

    if (!organizationOptions.length) return fail('No organization options are configured');
    const matchedOrganizationName = matchConfiguredOption(organizationName, organizationOptions);
    if (!matchedOrganizationName) return fail(`Organization "${organizationName}" is not configured`);

    let matchedSiteName = '';
    if (siteName) {
      if (!siteOptions.length) return fail('No site options are configured');
      matchedSiteName = matchConfiguredOption(siteName, siteOptions);
      if (!matchedSiteName) return fail(`Site "${siteName}" is not configured`);
    }

	    if (!accessReason) return fail('Reason is required');

    return {
      rowNumber: row.rowNumber,
      requestedName,
      emailAddress,
	      roleName: matchedRoleName,
	      organizationName: matchedOrganizationName,
	      siteName: matchedSiteName,
	      accessReason,
      error: ''
    };
  };

  const importPreviewRows = importRows.map((row) => {
    const prepared = prepareImportRow(row);
    const serverError = importServerErrors[row.rowNumber] || '';
    const error = prepared.error || serverError;
    return {
      row,
      prepared,
      error
    };
  });
  const invalidImportCount = importPreviewRows.filter((item) => Boolean(item.error)).length;
  const importableCount = importPreviewRows.length - invalidImportCount;
  const nextBatchRowNumber = React.useMemo(() => {
    if (!importRows.length) return 2;
    return Math.max(...importRows.map((row) => row.rowNumber)) + 1;
  }, [importRows]);
  const batchReadyCount = importableCount;
  const batchNeedsFixCount = invalidImportCount;

  const setImportRowField = (rowNumber: number, field: ImportEditableField, value: string) => {
    setImportRows((prev) => prev.map((row) => {
      if (row.rowNumber !== rowNumber) return row;
      return {
        ...row,
        [field]: value
      };
    }));

    setImportServerErrors((prev) => {
      if (!prev[rowNumber]) return prev;
      const next = { ...prev };
      delete next[rowNumber];
      return next;
    });
  };

  const clearImportDialogState = () => {
    setImportRows([]);
    setImportSourceName('');
    setImportServerErrors({});
  };

  const closeImportDialog = () => {
    if (isImporting) return;
    setIsImportDialogOpen(false);
    clearImportDialogState();
  };

  const openNewRequestDialog = () => {
    setIsNewRequestDialogOpen(true);
  };

  const openImportFilePicker = (destination: ImportDestination = 'review') => {
    setImportDestination(destination);
    importFileInputRef.current?.click();
  };

	  const resetBatchDraft = () => {
	    setBatchDraft(emptyBatchDraft);
	  };

  const addBatchDraftRow = () => {
    const row: ImportDraftRow = {
      rowNumber: nextBatchRowNumber,
      requestedName: batchDraft.requestedName,
      emailAddress: batchDraft.emailAddress,
      roleName: batchDraft.roleName,
      organizationName: batchDraft.organizationName,
      siteName: batchDraft.siteName,
      accessReason: truncateReason(batchDraft.accessReason)
    };
    const hasData = [
      row.requestedName,
      row.emailAddress,
	      row.roleName,
	      row.organizationName,
	      row.siteName,
	      row.accessReason
    ].some((value) => cleanImportValue(value).length > 0);
    if (!hasData) {
      toast.error('Enter at least one field before adding a row');
      return;
    }

    setImportRows((prev) => [...prev, row]);
    setImportSourceName((prev) => prev || 'Manual batch list');
    resetBatchDraft();
  };

  const removeBatchRow = (rowNumber: number) => {
    setImportRows((prev) => prev.filter((row) => row.rowNumber !== rowNumber));
    setImportServerErrors((prev) => {
      if (!prev[rowNumber]) return prev;
      const next = { ...prev };
      delete next[rowNumber];
      return next;
    });
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (optionsLoading) {
      toast.error('Wait for role, organization, and site options to finish loading');
      return;
    }

    try {
      const fileBytes = await file.arrayBuffer();
      const workbook = XLSX.read(fileBytes, { type: 'array', cellDates: false });
      const firstSheetName = workbook.SheetNames?.[0];
      if (!firstSheetName) {
        toast.error('Spreadsheet does not contain any worksheet');
        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });
      if (!rows.length) {
        toast.error('Spreadsheet has no data rows');
        return;
      }

      const parsedRows: ImportDraftRow[] = [];
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index] || {};
        const rowNumber = index + 2;
        const requestedName = getImportRowValue(row, IMPORT_COLUMN_ALIASES.requestedName);
        const emailAddress = getImportRowValue(row, IMPORT_COLUMN_ALIASES.emailAddress);
	        const roleName = getImportRowValue(row, IMPORT_COLUMN_ALIASES.roleName);
	        const organizationName = getImportRowValue(row, IMPORT_COLUMN_ALIASES.organizationName);
	        const siteName = getImportRowValue(row, IMPORT_COLUMN_ALIASES.siteName);
        const accessReason = truncateReason(getImportRowValue(row, IMPORT_COLUMN_ALIASES.accessReason));
	        const hasData = [requestedName, emailAddress, roleName, organizationName, siteName, accessReason].some((value) => value.length > 0);
        if (!hasData) continue;

        parsedRows.push({
          rowNumber,
          requestedName,
	          emailAddress,
		          roleName,
		          organizationName,
		          siteName,
	          accessReason
	        });
      }

      if (!parsedRows.length) {
        toast.error('Spreadsheet has no importable data rows');
        return;
      }

      if (importDestination === 'batch') {
        const rowsForBatch = parsedRows.map((row, index) => ({
          ...row,
          rowNumber: nextBatchRowNumber + index
        }));
        setImportRows((prev) => [...prev, ...rowsForBatch]);
        setImportSourceName((prev) => {
          if (!prev) return file.name;
          if (prev.includes(file.name)) return prev;
          return `${prev}, ${file.name}`;
        });
	        setImportServerErrors({});
	        setIsNewRequestDialogOpen(true);
        toast.success(`Added ${rowsForBatch.length} ${rowsForBatch.length === 1 ? 'row' : 'rows'} from ${file.name}`);
        return;
      }

      setImportRows(parsedRows);
      setImportSourceName(file.name);
      setImportServerErrors({});
      setIsImportDialogOpen(true);
    } catch (importError: any) {
      toast.error(importError?.message || 'Failed to parse spreadsheet');
    }
  };

  const submitImportRows = async () => {
    if (!importRows.length) {
      toast.error('No rows to import');
      return;
    }

    const preparedRows = importRows.map((row) => prepareImportRow(row));
    const firstValidationError = preparedRows.find((row) => row.error);
    if (firstValidationError) {
      toast.error(`Row ${firstValidationError.rowNumber}: ${firstValidationError.error}`);
      return;
    }

    setIsImporting(true);
    try {
      const failures: string[] = [];
      const failureByRow: Record<number, string> = {};
      const successfulRows: number[] = [];

      for (let index = 0; index < preparedRows.length; index += IMPORT_REQUEST_CONCURRENCY) {
        const batch = preparedRows.slice(index, index + IMPORT_REQUEST_CONCURRENCY);
        const batchResults = await Promise.all(
          batch.map(async (row) => {
            try {
              const response = await createRequest({
                variables: {
	                  input: {
	                    requestType: REQUEST_TYPE_ACTIVATION,
	                    requestedName: row.requestedName,
	                    emailAddress: row.emailAddress,
	                    organizationName: row.organizationName,
	                    siteName: row.siteName || null,
	                    roleName: row.roleName,
	                    accessReason: row.accessReason
                  }
                }
              });

              const result = response?.data?.createUserAccountRequest;
              if (!result?.success) {
                return {
                  rowNumber: row.rowNumber,
                  success: false,
                  message: result?.message || 'Unable to create request'
                };
              }

              return {
                rowNumber: row.rowNumber,
                success: true,
                message: ''
              };
            } catch (rowError: any) {
              return {
                rowNumber: row.rowNumber,
                success: false,
                message: rowError?.message || 'Unable to create request'
              };
            }
          })
        );

        batchResults.forEach((batchResult) => {
          if (batchResult.success) {
            successfulRows.push(batchResult.rowNumber);
            return;
          }

          failures.push(`Row ${batchResult.rowNumber}: ${batchResult.message}`);
          failureByRow[batchResult.rowNumber] = batchResult.message;
        });
      }

      const successCount = successfulRows.length;
      const failureCount = failures.length;

      if (successCount > 0) {
        const successfulSet = new Set(successfulRows);
        setImportRows((prev) => prev.filter((row) => !successfulSet.has(row.rowNumber)));
        await refetch();
      }
      setImportServerErrors(failureByRow);

      if (successCount === 0 && failureCount > 0) {
        toast.error(failures[0]);
        return;
      }

      if (successCount === 0) {
        toast.error('No rows were imported');
        return;
      }

      const requestLabel = successCount === 1 ? 'request' : 'requests';
	      if (failureCount === 0) {
	        toast.success(`Imported ${successCount} ${requestLabel}`);
	        setIsImportDialogOpen(false);
	        setIsNewRequestDialogOpen(false);
	        clearImportDialogState();
	        return;
      }

      toast.success(`Imported ${successCount} ${requestLabel} with ${failureCount} failed row(s)`);
      toast.error(`First import error: ${failures[0]}`);
    } finally {
      setIsImporting(false);
    }
  };

  const submitBatchRows = async () => {
    await submitImportRows();
  };

  return (
    <div className="mx-auto max-w-[1600px] space-y-5 px-4 py-4">
      <input
        ref={importFileInputRef}
        type="file"
        className="hidden"
        accept=".xls,.xlsx"
        onChange={handleImportFile}
      />

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="space-y-1">
              <CardTitle className="text-lg">My Submitted Requests</CardTitle>
              <CardDescription>Total: {totalCount}</CardDescription>
            </div>
            <div className="flex flex-col items-start gap-2 md:items-end">
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" onClick={openNewRequestDialog} disabled={submitting || optionsLoading}>
                  New User Request
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openImportFilePicker('review')}
                  disabled={isImporting || submitting || optionsLoading}
                >
                  {isImporting ? 'Importing...' : 'Import XLS'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={openBulkDeleteDialog}
                  disabled={cancelingRequest || selectedPendingRequestIds.length === 0}
                >
                  {`Delete Selected (${selectedPendingRequestIds.length})`}
                </Button>
                <Button variant="outline" onClick={() => refetch()}>
                  Refresh
                </Button>
              </div>
              <p className="max-w-[720px] text-xs text-[var(--neutral-gray-80)]">
                Download{' '}
                <a
                  className="font-medium text-[var(--azure-primary)] hover:text-[var(--azure-primary-hover)] hover:underline"
                  href={`${process.env.PUBLIC_URL || ''}/docs/UserRequestTemplate.xls`}
                  download
                >
                  UserRequestTemplate.xls
                </a>{' '}
                and fill in the columns before import.
              </p>
	              <p className="max-w-[720px] text-xs text-[var(--neutral-gray-80)]">
	                Use <strong>New User Request</strong> to build a list manually or load rows from the XLS template. <strong>Import XLS</strong> opens the review popup directly.
	              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search requests"
                className="w-56"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="deactivated">Deactivated</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-52">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roleOptions.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
	              <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="Organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All organizations</SelectItem>
                  {organizationOptions.map((organization) => (
                    <SelectItem key={organization} value={organization}>
                      {organization}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sites</SelectItem>
                  {siteOptions.map((site) => (
                    <SelectItem key={site} value={site}>
                      {site}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

	            <p className="text-xs text-[var(--neutral-gray-80)]">
	              Uses columns User Name, User Email, Organization, Site, Role, and Reason.
	            </p>
          </div>

          {error && (
            <Alert className="mt-0" variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          <div className="overflow-x-auto rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)]">
            <table className="w-full text-xs">
              <thead className="bg-[var(--surface-muted)]">
                <tr>
                  <th className="w-10 px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">
                    <input
                      type="checkbox"
                      aria-label="Select all pending requests"
                      checked={allPendingSelected}
                      onChange={(event) => setAllPendingRequestsSelected(event.target.checked)}
                      disabled={cancelingRequest || updatingRequest || pendingRequestIdsOnPage.length === 0}
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Requested User</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Requester</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Email</th>
	                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Organization</th>
	                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Site</th>
	                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Created</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Notes</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
	                    <td className="px-3 py-3 text-[var(--neutral-gray-80)]" colSpan={12}>
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && requests.length === 0 && (
                  <tr>
	                    <td className="px-3 py-3 text-[var(--neutral-gray-80)]" colSpan={12}>
                      No requests found.
                    </td>
                  </tr>
                )}
                {!loading && requests.map((row) => (
                  <tr
                    key={row.id}
                    className={row.status === 'pending'
                      ? 'border-t border-[var(--neutral-gray-30)] bg-[var(--semantic-warning-soft)]'
                      : 'border-t border-[var(--neutral-gray-30)]'}
                  >
                    <td className="px-3 py-2 align-middle">
                      {row.status === 'pending' && (
                        <input
                          type="checkbox"
                          aria-label={`Select request #${row.id}`}
                          checked={selectedPendingRequestIds.includes(row.id)}
                          onChange={(event) => setPendingRequestSelected(row.id, event.target.checked)}
                          disabled={cancelingRequest || updatingRequest}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.requestedName}</td>
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.requesterName}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.emailAddress}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.organizationName}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.siteName || '-'}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.roleName}</td>
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{requestTypeLabel(row.requestType)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${requestStatusBadgeClass(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{formatDate(row.createdAt)}</td>
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]" title={isDeactivationRequest(row.requestType) ? row.deactivationReason || row.accessReason : row.accessReason}>
                      {truncate(isDeactivationRequest(row.requestType) ? row.deactivationReason || row.accessReason : row.accessReason)}
                    </td>
                    <td className="px-3 py-2">
                      {row.status === 'pending' && (
                        <div className="flex items-center gap-2">
                          {!isDeactivationRequest(row.requestType) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 border-[var(--azure-primary-border-soft)] px-0 text-[var(--azure-primary)] hover:bg-[var(--azure-primary-soft)] hover:text-[var(--azure-primary-hover)]"
                              disabled={cancelingRequest || updatingRequest}
                              onClick={() => openEditDialog(row)}
                              aria-label={`Edit request #${row.id}`}
                              title={`Edit request #${row.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 border-[var(--semantic-error-border-soft)] px-0 text-[var(--semantic-error)] hover:bg-[var(--semantic-error-soft)] hover:text-[var(--semantic-error)]"
                            disabled={cancelingRequest || updatingRequest}
                            onClick={() => openDeleteDialog(row.id)}
                            aria-label={`Delete request #${row.id}`}
                            title={`Delete request #${row.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {row.status !== 'pending' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 px-0 text-[var(--neutral-gray-60)]"
                          disabled
                          aria-label={`No actions for request #${row.id}`}
                          title={`No actions for request #${row.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={isNewRequestDialogOpen}
        onOpenChange={(open: boolean) => {
          setIsNewRequestDialogOpen(open);
        }}
      >
        <DialogContent
          className="gap-0 overflow-hidden p-0"
          contentWidth="96vw"
          contentHeight="auto"
          contentMaxWidth="1600px"
          contentMaxHeight="86vh"
        >
	          <DialogHeader className="gap-1 border-b border-[var(--neutral-gray-30)] px-5 py-4 pr-10">
	            <DialogTitle>New User Account Request</DialogTitle>
	            <DialogDescription>
	              Add one or more account requests. Requester details are captured from your current login.
	            </DialogDescription>
	          </DialogHeader>

	          <div className="max-h-[calc(86vh-90px)] space-y-4 overflow-y-auto px-5 py-4">
              {optionsError && (
                <Alert variant="destructive">
                  <AlertDescription>{optionsError.message}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
	                    <h3 className="text-sm font-semibold text-[var(--neutral-gray-110)]">Add Requests</h3>
                    <p className="text-xs text-[var(--neutral-gray-80)]">
                      Add one row at a time, or load rows from the XLS template into the list below.
                    </p>
                  </div>
                  <Button
                    id="request-batch-upload-xls-button"
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => openImportFilePicker('batch')}
                    disabled={isImporting || submitting || optionsLoading}
                  >
                    <Upload className="h-4 w-4" />
                    Load XLS
                  </Button>
                </div>

	                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="request-batch-name-input">
                      Name
                    </label>
                    <Input
                      id="request-batch-name-input"
                      value={batchDraft.requestedName}
                      onChange={(event) => setBatchDraftField('requestedName', event.target.value)}
                      placeholder="Requested user full name"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="request-batch-email-input">
                      Email Address
                    </label>
                    <Input
                      id="request-batch-email-input"
                      type="email"
                      value={batchDraft.emailAddress}
                      onChange={(event) => setBatchDraftField('emailAddress', event.target.value)}
                      placeholder="user@example.org"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Organization Name</label>
                    <Select
                      value={batchDraft.organizationName}
                      onValueChange={(value) => setBatchDraftField('organizationName', value)}
                    >
                      <SelectTrigger disabled={optionsLoading || organizationOptions.length === 0}>
                        <SelectValue placeholder={optionsLoading ? 'Loading organizations...' : 'Select an organization'} />
                      </SelectTrigger>
                      <SelectContent>
                        {organizationOptions.map((organization) => (
                          <SelectItem key={organization} value={organization}>
                            {organization}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Site</label>
                    <Select
                      value={batchDraft.siteName || '__none'}
                      onValueChange={(value) => setBatchDraftField('siteName', value === '__none' ? '' : value)}
                    >
                      <SelectTrigger disabled={optionsLoading || siteOptions.length === 0}>
                        <SelectValue placeholder={optionsLoading ? 'Loading sites...' : 'Select a site'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">No site assigned</SelectItem>
                        {siteOptions.map((site) => (
                          <SelectItem key={site} value={site}>
                            {site}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

	                  <div>
	                    <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
	                    <Select value={batchDraft.roleName} onValueChange={(value) => setBatchDraftField('roleName', value)}>
	                      <SelectTrigger disabled={optionsLoading || roleOptions.length === 0}>
	                        <SelectValue placeholder={optionsLoading ? 'Loading roles...' : 'Select a role'} />
	                      </SelectTrigger>
	                      <SelectContent>
	                        {roleOptions.map((role) => (
	                          <SelectItem key={role} value={role}>
	                            {role}
	                          </SelectItem>
	                        ))}
	                      </SelectContent>
	                    </Select>
	                  </div>

	                  <div>
	                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="request-batch-reason-input">
	                      Reason for request
	                    </label>
                    <Input
                      id="request-batch-reason-input"
                      value={batchDraft.accessReason}
                      onChange={(event) => setBatchDraftField('accessReason', event.target.value)}
                      placeholder="Business need"
                      maxLength={30}
                    />
                  </div>
	                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    id="request-batch-add-row-button"
                    type="button"
                    onClick={addBatchDraftRow}
                    disabled={submitting || isImporting || optionsLoading}
                  >
                    <Plus className="h-4 w-4" />
                    Add to List
                  </Button>
                  <Button type="button" variant="outline" onClick={resetBatchDraft} disabled={submitting || isImporting}>
                    Clear Entry
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--neutral-gray-90)]">
                    <span className="rounded-full bg-[var(--surface-muted)] px-2 py-1">
                      {importRows.length} in list
                    </span>
                    <span className="rounded-full bg-[var(--semantic-success-soft)] px-2 py-1 text-[var(--semantic-success)]">
                      {batchReadyCount} ready
                    </span>
                    <span className={batchNeedsFixCount > 0
                      ? 'rounded-full bg-[var(--semantic-error-soft)] px-2 py-1 text-[var(--semantic-error)]'
                      : 'rounded-full bg-[var(--surface-muted)] px-2 py-1 text-[var(--neutral-gray-80)]'}
                    >
                      {batchNeedsFixCount} needs fix
                    </span>
                    {importSourceName && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-muted)] px-2 py-1">
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                        {importSourceName}
                      </span>
                    )}
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={clearImportDialogState}
                    disabled={isImporting || importRows.length === 0}
                  >
                    Clear List
                  </Button>
                </div>

                <div className="max-h-[36vh] overflow-auto rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)]">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[var(--surface-muted)]">
                      <tr>
                        <th className="p-1.5 text-left">Row</th>
                        <th className="p-1.5 text-left">User Name</th>
                        <th className="p-1.5 text-left">User Email</th>
	                        <th className="p-1.5 text-left">Organization</th>
	                        <th className="p-1.5 text-left">Site</th>
	                        <th className="p-1.5 text-left">Role</th>
                        <th className="p-1.5 text-left">Reason</th>
                        <th className="p-1.5 text-left">Error</th>
                        <th className="p-1.5 text-left">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreviewRows.length === 0 && (
                        <tr>
	                          <td className="p-2 text-gray-500" colSpan={9}>
                            No rows added yet.
                          </td>
                        </tr>
                      )}
                      {importPreviewRows.map((item) => {
                        return (
                          <tr key={item.row.rowNumber} className="border-t border-[var(--neutral-gray-30)]">
                            <td className="p-1.5 align-top">{item.row.rowNumber}</td>
                            <td className="p-1.5">
                              <Input
                                id={`request-batch-row-${item.row.rowNumber}-name`}
                                value={item.row.requestedName}
                                onChange={(event) => setImportRowField(item.row.rowNumber, 'requestedName', event.target.value)}
                                className="h-7 min-w-[150px] px-2 text-xs"
                              />
                            </td>
                            <td className="p-1.5">
                              <Input
                                id={`request-batch-row-${item.row.rowNumber}-email`}
                                value={item.row.emailAddress}
                                onChange={(event) => setImportRowField(item.row.rowNumber, 'emailAddress', event.target.value)}
                                className="h-7 min-w-[190px] px-2 text-xs"
                              />
                            </td>
                            <td className="p-1.5">
                              <Input
                                value={item.row.organizationName}
                                onChange={(event) => setImportRowField(item.row.rowNumber, 'organizationName', event.target.value)}
                                className="h-7 min-w-[150px] px-2 text-xs"
                              />
                            </td>
                            <td className="p-1.5">
                              <Input
                                value={item.row.siteName}
                                onChange={(event) => setImportRowField(item.row.rowNumber, 'siteName', event.target.value)}
                                className="h-7 min-w-[135px] px-2 text-xs"
                              />
                            </td>
                            <td className="p-1.5">
                              <Input
                                value={item.row.roleName}
                                onChange={(event) => setImportRowField(item.row.rowNumber, 'roleName', event.target.value)}
                                className="h-7 min-w-[145px] px-2 text-xs"
                              />
                            </td>
                            <td className="p-1.5">
                              <Input
                                value={item.row.accessReason}
                                onChange={(event) => setImportRowField(item.row.rowNumber, 'accessReason', event.target.value)}
                                className="h-7 min-w-[160px] max-w-[190px] px-2 text-xs"
                                placeholder="Required"
                                maxLength={33}
                              />
                            </td>
                            <td className={item.error ? 'p-1.5 align-top text-xs leading-4 text-red-700 bg-red-50' : 'p-1.5 align-top text-xs leading-4 text-green-700'}>
                              {item.error || 'OK'}
                            </td>
                            <td className="p-1.5 align-top">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 px-0"
                                onClick={() => removeBatchRow(item.row.rowNumber)}
                                disabled={isImporting}
                                aria-label={`Remove row ${item.row.rowNumber}`}
                                title={`Remove row ${item.row.rowNumber}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

	                <div className="flex flex-wrap items-center justify-between gap-2">
	                  <p className="text-xs text-[var(--neutral-gray-80)]">
		                    Required columns: User Name, User Email, Organization, Role, and Reason. Site is optional.
	                  </p>
                  <DialogFooter className="justify-end">
                    <Button
                      id="request-batch-submit-button"
                      type="button"
                      onClick={submitBatchRows}
                      disabled={isImporting || importRows.length === 0 || batchNeedsFixCount > 0}
                    >
                      <Send className="h-4 w-4" />
                      {isImporting ? 'Creating...' : `Create ${batchReadyCount} ${batchReadyCount === 1 ? 'Request' : 'Requests'}`}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsNewRequestDialogOpen(false)} disabled={isImporting}>
                      Close
                    </Button>
                  </DialogFooter>
                </div>
              </div>
	            </div>
        </DialogContent>
      </Dialog>

      <ImportReviewDialog
        open={isImportDialogOpen}
        disableClose={isImporting}
        title="Review Imported Rows"
        onOpenChange={(open: boolean) => {
          if (open) {
            setIsImportDialogOpen(true);
            return;
          }
          closeImportDialog();
        }}
      >
        <div className="space-y-3">
          <div className="text-xs text-[var(--neutral-gray-100)]">
            <strong>File:</strong> {importSourceName || 'N/A'} | <strong>Rows:</strong> {importPreviewRows.length} | <strong>Ready:</strong> {importableCount} | <strong>Needs Fix:</strong> {invalidImportCount}
          </div>

          <div className="max-h-[52vh] overflow-auto rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--surface-muted)]">
                <tr>
                  <th className="text-left p-1.5">Row</th>
                  <th className="text-left p-1.5">User Name</th>
                  <th className="text-left p-1.5">User Email</th>
	                  <th className="text-left p-1.5">Role</th>
	                  <th className="text-left p-1.5">Organization</th>
	                  <th className="text-left p-1.5">Site</th>
                  <th className="text-left p-1.5">Reason</th>
                  <th className="text-left p-1.5">Error</th>
                </tr>
              </thead>
              <tbody>
                {importPreviewRows.length === 0 && (
                  <tr>
	                    <td className="p-2 text-gray-500" colSpan={8}>No rows loaded.</td>
                  </tr>
                )}
                {importPreviewRows.map((item) => {
                  const nameError = importFieldHasError(item.error, ['user name']);
                  const emailError = importFieldHasError(item.error, ['user email']);
	                  const roleError = importFieldHasError(item.error, ['role']);
	                  const organizationError = importFieldHasError(item.error, ['organization']);
	                  const siteError = importFieldHasError(item.error, ['site']);
                  const reasonError = importFieldHasError(item.error, ['reason']);

                  return (
                    <tr key={item.row.rowNumber} className="border-t border-[var(--neutral-gray-30)]">
                      <td className="p-1.5 align-top">{item.row.rowNumber}</td>
                      <td className="p-1.5">
                        <Input
                          value={item.row.requestedName}
                          onChange={(event) => setImportRowField(item.row.rowNumber, 'requestedName', event.target.value)}
                          className={`h-7 min-w-[160px] text-xs px-2 ${nameError ? 'border-red-500 bg-red-50' : ''}`}
                        />
                      </td>
                      <td className="p-1.5">
                        <Input
                          value={item.row.emailAddress}
                          onChange={(event) => setImportRowField(item.row.rowNumber, 'emailAddress', event.target.value)}
                          className={`h-7 min-w-[210px] text-xs px-2 ${emailError ? 'border-red-500 bg-red-50' : ''}`}
                        />
                      </td>
                      <td className="p-1.5">
                        <Input
                          value={item.row.roleName}
                          onChange={(event) => setImportRowField(item.row.rowNumber, 'roleName', event.target.value)}
                          className={`h-7 min-w-[165px] text-xs px-2 ${roleError ? 'border-red-500 bg-red-50' : ''}`}
                        />
                      </td>
                      <td className="p-1.5">
                        <Input
                          value={item.row.organizationName}
                          onChange={(event) => setImportRowField(item.row.rowNumber, 'organizationName', event.target.value)}
                          className={`h-7 min-w-[165px] text-xs px-2 ${organizationError ? 'border-red-500 bg-red-50' : ''}`}
                        />
                      </td>
                      <td className="p-1.5">
                        <Input
                          value={item.row.siteName}
                          onChange={(event) => setImportRowField(item.row.rowNumber, 'siteName', event.target.value)}
                          className={`h-7 min-w-[165px] text-xs px-2 ${siteError ? 'border-red-500 bg-red-50' : ''}`}
                        />
                      </td>
	                      <td className="p-1.5">
                        <Input
                          value={item.row.accessReason}
                          onChange={(event) => setImportRowField(item.row.rowNumber, 'accessReason', event.target.value)}
                          className={`h-7 min-w-[220px] text-xs px-2 ${reasonError ? 'border-red-500 bg-red-50' : ''}`}
                          placeholder="Required"
                        />
                      </td>
                      <td className={item.error ? 'p-1.5 text-xs leading-4 text-red-700 bg-red-50 align-top' : 'p-1.5 text-xs leading-4 text-green-700 align-top'}>
                        {item.error || 'OK'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

	          <div className="flex flex-wrap items-center justify-between gap-2">
	            <p className="text-xs text-[var(--neutral-gray-80)]">
	              Organization and role templates assign access during provisioning. Reason is required for each row.
	            </p>
            <DialogFooter className="justify-end">
              <Button
                type="button"
                size="sm"
                onClick={submitImportRows}
                disabled={isImporting || importPreviewRows.length === 0 || invalidImportCount > 0}
              >
                {isImporting ? 'Importing...' : `Import ${importableCount} ${importableCount === 1 ? 'Row' : 'Rows'}`}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={closeImportDialog} disabled={isImporting}>
                Close
              </Button>
            </DialogFooter>
          </div>
        </div>
      </ImportReviewDialog>

      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open: boolean) => {
          setIsEditDialogOpen(open);
          if (open) return;
          setEditingRequest(null);
          setEditRoleName('');
          setEditSiteName('');
          setEditAccessReason('');
        }}
      >
        <DialogContent className="gap-0 overflow-hidden p-0" contentMaxWidth="640px" contentHeight="auto">
          <DialogHeader className="gap-1 border-b border-[var(--neutral-gray-30)] px-5 py-4 pr-10">
            <DialogTitle>
              {editingRequest ? `Edit Request #${editingRequest.id}` : 'Edit Request'}
            </DialogTitle>
            <DialogDescription>Update the role and request notes before the account is provisioned.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 py-4">
	            <div className="space-y-1 rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] p-3 text-sm text-[var(--neutral-gray-100)]">
	              <div><strong>User:</strong> {editingRequest?.requestedName || 'N/A'}</div>
	              <div><strong>Email:</strong> {editingRequest?.emailAddress || 'N/A'}</div>
	              <div><strong>Organization:</strong> {editingRequest?.organizationName || 'N/A'}</div>
	              <div><strong>Site:</strong> {editingRequest?.siteName || 'N/A'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <Select value={editRoleName} onValueChange={setEditRoleName}>
                <SelectTrigger disabled={optionsLoading || roleOptions.length === 0}>
                  <SelectValue placeholder={optionsLoading ? 'Loading roles...' : 'Select a role'} />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
              <Select
                value={editSiteName || '__none'}
                onValueChange={(value) => setEditSiteName(value === '__none' ? '' : value)}
              >
                <SelectTrigger disabled={optionsLoading || siteOptions.length === 0}>
                  <SelectValue placeholder={optionsLoading ? 'Loading sites...' : 'Select a site'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No site assigned</SelectItem>
                  {siteOptions.map((site) => (
                    <SelectItem key={site} value={site}>
                      {site}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <Textarea
                value={editAccessReason}
                onChange={(event) => setEditAccessReason(event.target.value)}
                placeholder="Enter notes for this access request"
                rows={4}
              />
            </div>

            <DialogFooter className="justify-end">
              <Button type="button" disabled={updatingRequest} onClick={submitEditRequest}>
                {updatingRequest ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(open: boolean) => {
          setIsDeleteDialogOpen(open);
          if (open) return;
          setPendingDeleteRequestId(null);
          setDeleteReason('');
        }}
      >
        <DialogContent className="gap-0 overflow-hidden p-0" contentMaxWidth="560px" contentHeight="auto">
          <DialogHeader className="gap-1 border-b border-[var(--neutral-gray-30)] px-5 py-4 pr-10">
            <DialogTitle>
              Delete Request {pendingDeleteRequestId ? `#${pendingDeleteRequestId}` : ''}
            </DialogTitle>
            <DialogDescription>Delete notes are required for audit history.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delete Notes</label>
              <Textarea
                value={deleteReason}
                onChange={(event) => setDeleteReason(event.target.value)}
                placeholder="Enter why this request is being deleted"
                rows={4}
              />
            </div>
            <DialogFooter className="justify-end">
              <Button type="button" variant="destructive" disabled={cancelingRequest || !pendingDeleteRequestId} onClick={confirmDeleteRequest}>
                {cancelingRequest ? 'Deleting...' : 'Delete'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isBulkDeleteDialogOpen}
        onOpenChange={(open: boolean) => {
          setIsBulkDeleteDialogOpen(open);
          if (open) return;
          setBulkDeleteReason('');
        }}
      >
        <DialogContent className="gap-0 overflow-hidden p-0" contentMaxWidth="560px" contentHeight="auto">
          <DialogHeader className="gap-1 border-b border-[var(--neutral-gray-30)] px-5 py-4 pr-10">
            <DialogTitle>
              Delete {selectedPendingRequestIds.length} Selected Request{selectedPendingRequestIds.length === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription>Delete notes are required and will be applied to each selected request.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delete Notes</label>
              <Textarea
                value={bulkDeleteReason}
                onChange={(event) => setBulkDeleteReason(event.target.value)}
                placeholder="Enter why these requests are being deleted"
                rows={4}
              />
            </div>
            <DialogFooter className="justify-end">
              <Button type="button" variant="destructive" disabled={cancelingRequest || selectedPendingRequestIds.length === 0} onClick={confirmBulkDeleteRequests}>
                {cancelingRequest ? 'Deleting...' : 'Delete Selected'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsBulkDeleteDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ProvisionUserAccountsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedRequest, setSelectedRequest] = React.useState<UserAccountRequestRow | null>(null);
  const [resultMessage, setResultMessage] = React.useState('');
  const [isRequestDialogOpen, setIsRequestDialogOpen] = React.useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [pendingCancelRequestId, setPendingCancelRequestId] = React.useState<number | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');

	  const { data, loading, error, refetch } = useQuery<UserAccountRequestsResponse>(LIST_ACCOUNT_REQUESTS, {
	    variables: {
	      filters: {
	        status: 'pending',
        search: searchTerm.trim(),
        limit: 200,
        offset: 0
      }
    },
	    fetchPolicy: 'cache-and-network'
	  });
  const { data: optionData } = useQuery<UserAccountRequestOptionsResponse>(LIST_USER_ACCOUNT_REQUEST_OPTIONS, {
    fetchPolicy: 'cache-and-network'
  });
	  const { data: aliasData, refetch: refetchAliases } = useQuery<{ listPolicyAliases: PolicyAliasRecord[] }>(LIST_POLICY_ALIASES, {
	    fetchPolicy: 'cache-and-network'
	  });

  const [convertRequest, { loading: converting }] = useMutation<ConvertUserAccountResponse>(CONVERT_ACCOUNT_REQUEST);
  const [cancelRequest, { loading: cancelingRequest }] = useMutation<CancelUserAccountResponse>(CANCEL_ACCOUNT_REQUEST);

	  const requests = data?.userAccountRequests?.entries || [];
	  const aliasRecords = React.useMemo(() => aliasData?.listPolicyAliases || [], [aliasData?.listPolicyAliases]);
  const organizationProgramTemplates = React.useMemo(() => {
    return buildOrganizationProgramTemplatesFromOptions(optionData?.organizationOptions);
  }, [optionData?.organizationOptions]);
	  const matchedOrganizationalAlias = React.useMemo(() => {
    if (!selectedRequest || isDeactivationRequest(selectedRequest.requestType)) {
      return null;
    }

    return findMatchingOrganizationalAlias(
      aliasRecords,
      selectedRequest.organizationName,
      selectedRequest.roleName
    );
  }, [aliasRecords, selectedRequest]);
  const initialSelectedPolicies = React.useMemo(() => {
    if (!selectedRequest || isDeactivationRequest(selectedRequest.requestType)) {
      return [];
    }

    const organizationalPolicies = matchedOrganizationalAlias
      ? matchedOrganizationalAlias.policies || []
      : [];

	    return Array.from(new Set([
	      ...buildHandshakePoliciesForRequest(selectedRequest, organizationProgramTemplates),
	      ...organizationalPolicies.map((policy) => `${policy || ''}`.trim()).filter((policy) => policy.length > 0)
	    ]));
	  }, [matchedOrganizationalAlias, organizationProgramTemplates, selectedRequest]);

  const handleConvert = async (values: CreateUserDialogValues) => {
    if (!selectedRequest) {
      toast.error('Select a request first');
      return;
    }
    if (isDeactivationRequest(selectedRequest.requestType)) {
      toast.error('Use Process Deactivation for this request');
      return;
    }

    try {
      const response = await convertRequest({
        variables: {
          input: {
            requestId: selectedRequest.id,
            username: values.username,
            emailAddress: values.emailAddress,
            organization: values.organization || null,
            site: values.site || null,
            twoFactorMethod: values.twoFactorMethod,
            legacyAccesses: values.legacyAccesses,
            selectedPolicies: values.selectedPolicies,
            activateNow: true
          }
        }
      });

      const result = response?.data?.convertUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to convert request');
        return;
      }

      const message = result?.message || `Created user ${result.username} (ID ${result.createdUserId}).`;

      setResultMessage(message);
      setIsCreateDialogOpen(false);
      toast.success('User created');
      setSelectedRequest(null);
      await refetch();
    } catch (convertError: any) {
      toast.error(convertError?.message || 'Failed to convert request');
    }
  };

  const handleProcessDeactivation = async () => {
    if (!selectedRequest) {
      toast.error('Select a request first');
      return;
    }
    if (!isDeactivationRequest(selectedRequest.requestType)) {
      toast.error('This request is not a deactivation request');
      return;
    }

    try {
      const response = await convertRequest({
        variables: {
          input: {
            requestId: selectedRequest.id
          }
        }
      });

      const result = response?.data?.convertUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to process deactivation request');
        return;
      }

      const message = result?.message || `Deactivated user ${result.username} (ID ${result.createdUserId}).`;
      setResultMessage(message);
      toast.success('User deactivated');
      setIsRequestDialogOpen(false);
      setSelectedRequest(null);
      await refetch();
    } catch (deactivationError: any) {
      toast.error(deactivationError?.message || 'Failed to process deactivation request');
    }
  };

  const handleCancelRequest = async (requestId: number, reason: string) => {
    try {
      const response = await cancelRequest({
        variables: { input: { requestId, cancelReason: reason } }
      });
      const result = response?.data?.cancelUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to cancel request');
        return;
      }

      toast.success(`Request #${requestId} canceled`);

      if (selectedRequest?.id === requestId) {
        setIsRequestDialogOpen(false);
        setSelectedRequest(null);
        setIsCreateDialogOpen(false);
      }

      await refetch();
    } catch (cancelError: any) {
      toast.error(cancelError?.message || 'Failed to cancel request');
    }
  };

  const openCancelDialog = (requestId: number) => {
    setPendingCancelRequestId(requestId);
    setCancelReason('');
    setIsCancelDialogOpen(true);
  };

  const confirmCancelRequest = async () => {
    if (!pendingCancelRequestId) return;
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error('Delete notes are required');
      return;
    }
    const requestId = pendingCancelRequestId;
    setPendingCancelRequestId(null);
    setCancelReason('');
    setIsCancelDialogOpen(false);
    await handleCancelRequest(requestId, reason);
  };

  const openCreateUserDialogForRequest = async (request: UserAccountRequestRow) => {
    if (isDeactivationRequest(request.requestType)) {
      return;
    }

    try {
      await refetchAliases();
    } catch (error) {
      console.error('Failed to refresh policy aliases before opening create dialog:', error);
    }

    setSelectedRequest(request);
    setResultMessage('');
    setIsRequestDialogOpen(false);
    setIsCreateDialogOpen(true);
  };

  const dialogContextPanel = selectedRequest ? (
	    <div className="rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--neutral-gray-100)]">
	      <div className="grid grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-4">
	        <div><strong>Request:</strong> #{selectedRequest.id}</div>
	        <div><strong>Requester:</strong> {selectedRequest.requesterName}</div>
	        <div><strong>Role:</strong> {selectedRequest.roleName}</div>
        <div><strong>User:</strong> {selectedRequest.requestedName}</div>
        <div><strong>Organization:</strong> {selectedRequest.organizationName}</div>
        <div><strong>Site:</strong> {selectedRequest.siteName || 'N/A'}</div>
        {matchedOrganizationalAlias && (
          <div><strong>Org Role Group:</strong> {formatOrganizationalAliasLabel(matchedOrganizationalAlias.alias)}</div>
        )}
        <div className="md:col-span-4">
          <strong>Reason:</strong>{' '}
          <span className="break-words">
            {isDeactivationRequest(selectedRequest.requestType)
              ? selectedRequest.deactivationReason || selectedRequest.accessReason || 'N/A'
              : selectedRequest.accessReason || 'N/A'}
          </span>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="mx-auto max-w-[1600px] space-y-5 px-4 py-4">
      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-lg">Provision Users from Requests</CardTitle>
              <CardDescription>
                Process pending activation and deactivation account requests.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search pending requests"
                className="w-64"
              />
              <Button variant="outline" onClick={() => refetch()}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          {error && (
            <Alert className="mt-0" variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          {resultMessage && (
            <Alert className="mt-0">
              <AlertDescription>{resultMessage}</AlertDescription>
            </Alert>
          )}

          <div className="overflow-x-auto rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)]">
            <table className="w-full text-xs">
              <thead className="bg-[var(--surface-muted)]">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">ID</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Requested User</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Email</th>
	                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Type</th>
	                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Organization</th>
	                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Site</th>
	                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Created</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-90)]">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
	                    <td className="px-3 py-3 text-[var(--neutral-gray-80)]" colSpan={8}>
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && requests.length === 0 && (
                  <tr>
	                    <td className="px-3 py-3 text-[var(--neutral-gray-80)]" colSpan={8}>
                      No pending requests found.
                    </td>
                  </tr>
                )}
                {!loading && requests.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-t border-[var(--neutral-gray-30)] hover:bg-[var(--surface-muted)]"
                    onClick={() => {
                      setSelectedRequest(row);
                      setResultMessage('');
                      if (isDeactivationRequest(row.requestType)) {
                        setIsCreateDialogOpen(false);
                        setIsRequestDialogOpen(true);
                        return;
                      }

                      setIsRequestDialogOpen(false);
                      setIsCreateDialogOpen(true);
                    }}
                  >
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.id}</td>
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.requestedName}</td>
                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.emailAddress}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{requestTypeLabel(row.requestType)}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.organizationName}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{row.siteName || '-'}</td>
	                    <td className="px-3 py-2 text-[var(--neutral-gray-110)]">{formatDate(row.createdAt)}</td>
                    <td className="px-3 py-2">
                      {!isDeactivationRequest(row.requestType) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mr-2 h-8 w-8 border-[var(--azure-primary-border-soft)] px-0 text-[var(--azure-primary)] hover:bg-[var(--azure-primary-soft)] hover:text-[var(--azure-primary-hover)]"
                          disabled={converting || cancelingRequest}
                          onClick={(event) => {
                            event.stopPropagation();
                            openCreateUserDialogForRequest(row);
                          }}
                          aria-label={`Create user for request #${row.id}`}
                          title={`Create user for request #${row.id}`}
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 border-[var(--semantic-error-border-soft)] px-0 text-[var(--semantic-error)] hover:bg-[var(--semantic-error-soft)] hover:text-[var(--semantic-error)]"
                        disabled={cancelingRequest}
                        onClick={(event) => {
                          event.stopPropagation();
                          openCancelDialog(row.id);
                        }}
                        aria-label={`Cancel request #${row.id}`}
                        title={`Cancel request #${row.id}`}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={isRequestDialogOpen}
        onOpenChange={(open: boolean) => {
          setIsRequestDialogOpen(open);
          if (open) return;
          if (isCreateDialogOpen) return;
          setSelectedRequest(null);
        }}
      >
        <DialogContent
          className="gap-0 overflow-hidden p-0"
          contentWidth="56vw"
          contentHeight="auto"
          contentMaxWidth="860px"
          contentMaxHeight="72vh"
        >
          {selectedRequest && (
            <div className="space-y-4">
              <DialogHeader className="gap-2 border-b border-[var(--neutral-gray-30)] px-5 py-4 pr-10">
                <DialogTitle>
                  {isDeactivationRequest(selectedRequest.requestType)
                    ? `Deactivation Request #${selectedRequest.id}`
                    : `Provision Request #${selectedRequest.id}`}
                </DialogTitle>
                <DialogDescription className="min-w-0 truncate">
                    {selectedRequest.requestedName} ({selectedRequest.emailAddress})
                </DialogDescription>
                <div className="flex items-center justify-end gap-2">
                  <div className="flex items-center justify-end gap-3 shrink-0">
                    {!isDeactivationRequest(selectedRequest.requestType) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-[var(--azure-primary-border-soft)] text-[var(--azure-primary)] hover:bg-[var(--azure-primary-soft)] hover:text-[var(--azure-primary-hover)]"
                        onClick={() => {
                          setIsRequestDialogOpen(false);
                          setIsCreateDialogOpen(true);
                        }}
                      >
                        <UserPlus className="h-4 w-4" />
                        Create User
                      </Button>
                    )}
                    {isDeactivationRequest(selectedRequest.requestType) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-[var(--azure-primary-border-soft)] text-[var(--azure-primary)] hover:bg-[var(--azure-primary-soft)] hover:text-[var(--azure-primary-hover)]"
                        disabled={converting}
                        onClick={handleProcessDeactivation}
                      >
                        <XCircle className="h-4 w-4" />
                        Process Deactivation
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-[var(--semantic-error-border-soft)] text-[var(--semantic-error)] hover:bg-[var(--semantic-error-soft)] hover:text-[var(--semantic-error)]"
                      disabled={cancelingRequest}
                      onClick={() => openCancelDialog(selectedRequest.id)}
                    >
                      <XCircle className="h-4 w-4" />
                      Cancel Request
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="mx-5 rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] p-4 text-sm text-[var(--neutral-gray-100)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
	                  <div><strong>Requester:</strong> {selectedRequest.requesterName}</div>
	                  <div><strong>Type:</strong> {requestTypeLabel(selectedRequest.requestType)}</div>
	                  <div><strong>Role:</strong> {selectedRequest.roleName}</div>
                  <div><strong>Organization:</strong> {selectedRequest.organizationName}</div>
                  <div><strong>Site:</strong> {selectedRequest.siteName || 'N/A'}</div>
                  {matchedOrganizationalAlias && (
                    <div><strong>Org Role Group:</strong> {formatOrganizationalAliasLabel(matchedOrganizationalAlias.alias)}</div>
                  )}
                  {isDeactivationRequest(selectedRequest.requestType) && (
                    <div><strong>Deactivation Date:</strong> {formatDate(selectedRequest.deactivationDate)}</div>
                  )}
                </div>
              </div>

              <div className="mx-5 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3">
                <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  {isDeactivationRequest(selectedRequest.requestType) ? 'Deactivation Reason' : 'Request Reason'}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--neutral-gray-100)]">
                  {isDeactivationRequest(selectedRequest.requestType)
                    ? selectedRequest.deactivationReason || selectedRequest.accessReason || 'N/A'
                    : selectedRequest.accessReason || 'N/A'}
                </p>
              </div>

              <DialogFooter className="justify-end px-5 pb-4">
                <Button type="button" variant="outline" onClick={() => setIsRequestDialogOpen(false)}>
                  Cancel
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CreateUserDialog
        open={Boolean(isCreateDialogOpen && selectedRequest && !isDeactivationRequest(selectedRequest.requestType))}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleConvert}
        submitting={converting}
        title={selectedRequest ? `Create User Account for Request #${selectedRequest.id}` : 'Create User Account'}
        description="Review the account settings and create the requested user."
        submitLabel="Create User Account"
        showRealNameField={false}
        contextPanel={dialogContextPanel}
        initialValues={selectedRequest && !isDeactivationRequest(selectedRequest.requestType) ? {
          emailAddress: selectedRequest.emailAddress || '',
          organization: selectedRequest.organizationName || '',
          site: selectedRequest.siteName || '',
          roleName: selectedRequest.roleName || '',
          legacyAccesses: buildLegacyAwareAssignments(initialSelectedPolicies).legacyPermissions.map((entry) => entry.permission),
          selectedPolicies: initialSelectedPolicies
        } : undefined}
      />

      <Dialog
        open={isCancelDialogOpen}
        onOpenChange={(open: boolean) => {
          setIsCancelDialogOpen(open);
          if (open) return;
          setPendingCancelRequestId(null);
          setCancelReason('');
        }}
      >
        <DialogContent className="gap-0 overflow-hidden p-0" contentMaxWidth="560px" contentHeight="auto">
          <DialogHeader className="gap-1 border-b border-[var(--neutral-gray-30)] px-5 py-4 pr-10">
            <DialogTitle>
              Cancel Request {pendingCancelRequestId ? `#${pendingCancelRequestId}` : ''}
            </DialogTitle>
            <DialogDescription>Delete notes are required for audit history.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delete Notes</label>
              <Textarea
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                placeholder="Enter why this request is being canceled"
                rows={4}
              />
            </div>
            <DialogFooter className="justify-end">
              <Button type="button" variant="destructive" disabled={cancelingRequest || !pendingCancelRequestId} onClick={confirmCancelRequest}>
                {cancelingRequest ? 'Canceling...' : 'Cancel Request'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsCancelDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestUserAccountPage;
